/**
 * Template Service
 *
 * Single source of truth for template discovery. Currently returns hardcoded
 * recipe-sourced templates. When the new template API is ready, swap the
 * implementation of `fetchAllTemplates()` to fetch from the API — consumers
 * (useTemplates hook, gallery panel) require no changes.
 *
 * The `#workflowname#` placeholder rewriting is handled at apply time in
 * useTemplateApply, not here, so templates returned by this service may
 * still contain placeholders.
 */

import type { TemplateDefinition } from '@/types/template';
import { BUILTIN_TEMPLATES } from '@/data/templates';

// =============================================================================
// PUBLIC API
// =============================================================================

/**
 * Fetch all available templates.
 *
 * Currently returns the hardcoded recipe-sourced built-in templates.
 * When the new template API is available, this function will fetch from
 * the API instead. The async signature is intentional — it keeps the
 * contract consistent so the switch is seamless.
 *
 * @param _queryScope  Workflow name used for `#workflowname#` placeholder
 *                     rewriting. Currently unused (hardcoded templates have
 *                     clean names), but will be needed by the API path.
 */
export async function fetchAllTemplates(
  _queryScope: string,
): Promise<TemplateDefinition[]> {
  // TODO: Replace with actual API fetch when the template API is ready.
  //
  // Example future implementation:
  // const recipes = await fetchJson<Recipe[]>(`${API_BASE}/templates`);
  // return recipes.map(r => rewritePlaceholders(recipeToTemplate(r), queryScope));

  return BUILTIN_TEMPLATES;
}
